/**
 * The numbers, names and addresses the shared UI is tuned by: the app-wide
 * providers (sign-in, the garden, beds, stages, the weather), the frame every
 * page sits in, and the toasts.
 *
 * Kept in one place, out of the components, so that each can be found and
 * changed without reading the component behind it, and so that they can
 * later come from the database instead (each carries its kept-values group, scripts/kept-values.md).
 * Constants only: no functions, and no imports, so anything may import this
 * without a cycle.
 *
 * Query keys and storage keys are tagged too, for uniformity, but they are
 * names: a storage key renamed forgets what every gardener's device had saved
 * under the old one, and a query key renamed must change at every reader and
 * every invalidation at once. Change them only on purpose.
 *
 * Not here: what the owner already changes in the Grow settings panel
 * (src/shared/lib/growSettings), the app-wide query default stale time
 * (src/config/queryClient QUERY_STALE_TIME_MS, which the header reuses), the
 * shared layer's names (tunables/shared: the outlook's and crop definitions'
 * query keys, the theme's storage key, Google's identity script, which the
 * providers here import from there), the tooltip timings
 * (src/shared/config/tooltipConfig, itself a config file) and the theme
 * palettes (src/shared/theme), which are config already.
 *
 * Used by: the shared contexts (Auth, Plants, Stages, Locations, Weather),
 * Layout, LoginPage, the toasts, and the screens that read
 * or invalidate the same query keys (the profile, locations, the photo tray,
 * packets, Today, sharing).
 *
 * @module tunables/ui
 */

// ---------------------------------------------------------------------------
// React Query keys the app-wide providers own
// ---------------------------------------------------------------------------

/**
 * The React Query key the garden's plants are cached under, followed by the
 * user and the postcode (PlantsContext). A write to a plant patches it by
 * this first word; a bed renamed, retired or deleted invalidates it
 * (LocationsContext), because every plant carries its bed's name.
 * @kept K1
 */
export const PLANTS_QUERY_KEY = 'plants';

/**
 * The React Query key the gardener's live beds are cached under
 * (LocationsContext). A plant saved invalidates it too (PlantsContext),
 * because a bed lists what is planted in it.
 * @kept K1
 */
export const LOCATIONS_QUERY_KEY = 'locations';

/**
 * The React Query key the archived plants are cached under (ArchivedPlants).
 * Any plant write invalidates it (PlantsContext refreshGarden).
 * @kept K1
 */
export const ARCHIVED_PLANTS_QUERY_KEY = 'archived-plants';

/**
 * The React Query key the growth stages, with the gardener's colours, are
 * cached under (StagesContext; Stage settings invalidates it after a change).
 * @kept K1
 */
export const STAGES_QUERY_KEY = 'stages';


// ---------------------------------------------------------------------------
// Freshness
// ---------------------------------------------------------------------------

/*
 * The plant picker's query key and freshness (PLANT_PICKER_QUERY_KEY, CATALOGUE_STALE_TIME_MS) live with its hooks,
 * in shared/components/plant-fields/usePlantPicker (W24 Job B: the catalogue is no longer downloaded whole).
 */

// ---------------------------------------------------------------------------
// Toasts
// ---------------------------------------------------------------------------

/**
 * How many toasts can be on screen at once (use-toast). A new one replaces
 * the last. A count.
 * @kept K5
 */
export const TOAST_LIMIT = 1;

/**
 * How long a dismissed toast is kept in the module-level store before it is
 * thrown away, in milliseconds (use-toast): long enough for its closing
 * animation. shadcn's default was seventeen minutes.
 * @kept K5
 */
export const TOAST_REMOVE_DELAY_MS = 5000;

/**
 * How long a toast stays up before it closes by itself, in milliseconds
 * (use-toast), unless it is given its own duration. Radix's own timer pauses
 * while the toast area is hovered or focused, and over an open window (the
 * Plant Doctor's "Added to notes") it never ran out, so the toast stayed
 * (owner, 2026-10-15: "the plant doctor added notes toast remains and does
 * not dismiss"); use-toast closes it on this timer as well.
 * @kept K5
 */
export const TOAST_DURATION_MS = 5000;

/**
 * How long a Copy button says "Copied" before it goes back to saying Copy,
 * in milliseconds (SharePlantDialog's link, PublicPageCard). Long enough to
 * read; short enough that a second copy later is not met with a tick that
 * was already there.
 * @kept K5
 */
export const COPIED_RESET_MS = 2000;

// ---------------------------------------------------------------------------
// Signing in
// ---------------------------------------------------------------------------

/**
 * sessionStorage: a Facebook sign-in Firebase refused because the email
 * already has an account by another way, kept across the redirect to be
 * joined to that account once they sign in (AuthContext).
 * @kept K1
 */
export const PENDING_SIGN_IN_LINK_SESSION_KEY = 'grow.pendingSignInLink';

/**
 * The media query that is true in the installed app (a home-screen PWA),
 * where there is no browser window for a sign-in popup, so Google and
 * Facebook go by redirect instead (AuthContext, LoginPage).
 * @kept K1
 */
export const STANDALONE_DISPLAY_MEDIA_QUERY = '(display-mode: standalone)';

/**
 * The path Firebase's sign-in handler is served at on the app's own host
 * (config/firebase, vite.config's /__ proxy). While the address contains it,
 * a sign-in is coming back and the frame says so rather than drawing the
 * sign-in form (Layout, LoginPage).
 * @kept K1
 */
export const FIREBASE_AUTH_HANDLER_PATH = '/__/auth/handler';

// ---------------------------------------------------------------------------
// The plant fields
// ---------------------------------------------------------------------------

/**
 * How many years back the photo date picker's year list reaches
 * (DateTakenField): far enough for an old garden photo, short enough to
 * scroll. A photo dated earlier still has its own year in the list.
 * @kept K5
 */
export const PHOTO_TAKEN_YEARS_BACK = 30;

// ---------------------------------------------------------------------------
// The account read
// ---------------------------------------------------------------------------

/**
 * How many times the account read (GET /users/firebase/:uid, AuthContext)
 * is tried when the server says "too many requests" (429) before the
 * standard failure page shows (the audit, 2026-10-16: a few quick page loads
 * tripped the server's limit, and one 429 showed the failure page).
 * @kept K1
 */
export const ACCOUNT_READ_TRIES = 3;

/**
 * How long to wait before reading the account again after a 429, when the
 * server's Retry-After does not say, and the longest wait taken even when it
 * says more. Milliseconds.
 * @kept K1
 */
export const ACCOUNT_READ_RETRY_MS = 2000;

/** The longest the wait between those retries grows to. Milliseconds. */
export const ACCOUNT_READ_RETRY_MAX_MS = 8000;
